/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import React, { Fragment } from 'react'
import { observer } from 'mobx-react'
import { UserDetailItem, UserPickWrap } from '@views/Home/index.style'
import { SPORT_ID_GRAY_ICON_MAP } from '@shared/SportIdGroup/Gray'
import dayjs from 'dayjs'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'
import { formatCreatedAtToElapsedTime } from '@utils/utils'
import { FEED_SUPPLIER, FROM_VIEW_TYPE, USER_COMMENT_TYPE } from '@v2/constants/common'
import {
  LS_REGULAR_ODDS_HANDICAP, LS_REGULAR_ODDS_UNDEROVER,
  UNIFIED_REGULAR_ODDS_HANDICAP,
  UNIFIED_REGULAR_ODDS_UNDEROVER,
} from '@game-type/game-market-types'
import { Button } from '@styles/button.style'
import { BET_HIT_STATE } from 'adv-protocol/src/GameCommon'
import { Theme } from '@themes/theme'
import { LikeButton } from '@components/Button/LikeButton'
import { CommentButton } from '@components/Button/CommentButton'
import { ArrowIco } from '@shared/Icon/ArrowIcon'
import useRootStore from '@store/useRootStore'
import PopupUtils from '@utils/popup-utils'
import { Schema$CompanyPick } from '@interface/company-pick'
import { Schema$UserComment } from '@interface/comment'
import AdvImg from '@components/AdvImg'

interface CompanyPickLikeActivityListItemProps {
  companyPick?: Schema$CompanyPick
  isDisabledLikeButton?: boolean
  disableUserInfoClick?: boolean
  className?: string
  isMatchupPageCall?: boolean
}

const CompanyPickLickContentsStyle = (theme: Theme) => css`
  background: ${theme.color.subColor};
  margin-top: 6px;  
  overflow: hidden;
  border-radius: 12px;
  ::after {
    display: block;
    content: '';
    width: 100%;
    height: 1px;
    box-shadow: 0 1px 1px 0 #40506030;
    background: ${theme.color.mainColor};
    position: absolute;
    bottom: 35px;
  }
  .pick-info-item {
    margin: 12px 14px;
    background: ${theme.color.mainColor};
    border-radius: 12px;
    padding: 10.5px 0 10px;
    position: relative;
    .top {
      display: flex;
      align-items: center;
      justify-content: center;
      height: ${theme.font.xxlarge};
      .outcome {
        color: ${theme.color.white};
        font-size: ${theme.font.title};
        font-weight: ${theme.fontW.medium};
        margin-right: 3px;
      }
      .rate {
        color: ${theme.color.yellow};
        font-size: ${theme.font.title};
        font-weight: ${theme.fontW.medium};
      }
    }
    .bottom {
      display: flex;
      align-items: center;
      justify-content: center;
      .market-type {
        line-height: ${theme.font.xlarge};
        color: ${theme.color.disableTxt2};
        font-size: ${theme.font.large};
        font-weight: ${theme.fontW.medium};
        margin-right: 3px;
      }
      .bet-point {
        line-height: ${theme.font.xlarge};
        color: ${theme.color.underover};
        font-size: ${theme.font.large};
      }
    }
    .state {
      display: block;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      position: absolute;
      top: 8px;
      right: 9px;
      &.success {
        background: ${theme.color.secondaryBg};
      }
      &.fail {
        background: ${theme.color.disableTxt};
      }
      &.cancel {
        background: ${theme.color.purple};
      }
    }
  }
  .detail-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 35px;    
    text-align: left;
    color: ${theme.color.btn};
    font-size: ${theme.font.medium};
    font-weight: ${theme.fontW.bold};
    line-height: 35px;
    padding-left: 18px;
    .btn-ico {
      width: auto;
      height: 6px;
      transform: rotate(-90deg) translateY(-30px);
      color: ${theme.color.btn};
      svg {
        width: auto;
        height: inherit;
      }
    }
  }
`

export const CompanyPickLikeActivityListItem: React.FC<CompanyPickLikeActivityListItemProps> = observer((props) => {
  const {
    companyPick,
    className,
    isDisabledLikeButton = true,
    disableUserInfoClick = true,
    isMatchupPageCall = false,
  } = props
  const { companyPickStore } = useRootStore()

  const matchInfoClass = classNames(
    'match-info row simple-info comment-list-match-info',
    { 'pc-info': !isMobile },
  )

  const handicapElement = <span className="market-type">핸디캡</span>
  const underOverElement = <span className="market-type">언더오버</span>

  let marketTypeElement
  if (companyPick.feedSupplier === FEED_SUPPLIER.BET_RADAR) {
    if (UNIFIED_REGULAR_ODDS_HANDICAP.indexOf(companyPick.marketType) >= 0) {
      marketTypeElement = handicapElement
    } else if (UNIFIED_REGULAR_ODDS_UNDEROVER.indexOf(companyPick.marketType) >= 0) {
      marketTypeElement = underOverElement
    }
  } else if (companyPick.feedSupplier === FEED_SUPPLIER.LSPORTS) {
    if (LS_REGULAR_ODDS_HANDICAP.indexOf(companyPick.marketType) >= 0) {
      marketTypeElement = handicapElement
    } else if (LS_REGULAR_ODDS_UNDEROVER.indexOf(companyPick.marketType) >= 0) {
      marketTypeElement = underOverElement
    }
  }

  let hitStateElement
  switch (companyPick.hitState) {
    case BET_HIT_STATE.SUCCESS:
      hitStateElement = <span className="success state"/>
      break
    case BET_HIT_STATE.FAIL:
      hitStateElement = <span className="fail state"/>
      break
    case BET_HIT_STATE.CANCEL:
      hitStateElement = <span className="cancel state"/>
      break
    default:
      break
  }

  const onClickCompanyPick = () => {
    companyPickStore.setIsMatchupPageCall(isMatchupPageCall)
    companyPickStore.setSelectedCompanyPick(companyPick)
    companyPickStore.fetchCompanyPickManagers({ userPromiseIds: [companyPick.userPromiseId] }).catch(console.error)
    PopupUtils.showCompanyPickDetailPopup()
  }

  const callbackCreated = (comment: Schema$UserComment): void => {
    PopupUtils.changePopupTitle('pick-comments-list', `댓글 ${companyPick.commentCount + 1}`)
    companyPickStore.createCommentCallback(comment)
  }

  const callbackDeleted = (comment: Schema$UserComment): void => {
    PopupUtils.changePopupTitle('pick-comments-list', `댓글 ${companyPick.commentCount - 1}`)
    companyPickStore.deleteCommentCallback(comment)
  }

  const onClickFavoriteCompanyPick = (): void => {
    if(isDisabledLikeButton) {
      return
    }

    companyPickStore.toggleCompanyPickLike(companyPick, false)
      .catch(err => console.error(err))
  }

  const enabled = companyPick.enabled

  const onClickCompanyPickCommentBtn = () => {
    if (!companyPick.enabled) {
      return
    }

    companyPickStore.setSelectedCompanyPick(companyPick)
    PopupUtils.showAdvComments({
      commentType: USER_COMMENT_TYPE.COMPANY_PICK_COMMENT,
      targetId: companyPick.id,
      callbackCreated,
      callbackDeleted,
      callbackUpdated: companyPickStore.updateCommentCallback,
      // callbackLiked: companyPickStore.updateAllCompanyPickCommentsLikeInfo,
      commentCount: companyPick.commentCount,
      fromViewType: FROM_VIEW_TYPE.COMPANY_PICK_COMMENT,
    })
  }

  let companyPickContents
  if (!enabled) {
    companyPickContents = <div className="block-message">삭제된 분석글입니다.</div>
  } else (
    companyPickContents = (
      <Fragment>
        <p className="title">{companyPick.title}</p>
        <Button className="pick-info" onClick={onClickCompanyPick} css={CompanyPickLickContentsStyle}>
          <div className="pick-info-item">
            <div className="top">
              <span className="outcome">{companyPick.selectionName}</span>
              <span className="rate">{companyPick.outcomeRate}</span>
            </div>
            <div className="bottom">
              {marketTypeElement}
              <span className="bet-point">{companyPick.specialBetValue}</span>
            </div>
            {hitStateElement}
          </div>
          <div className="detail-btn">
            자세히 보기
            <span className="btn-ico">
                <ArrowIco/>
              </span>
          </div>
        </Button>
      </Fragment>
    )
  )

  const onClickUserDetailInfoPopup = () => {
    if (!disableUserInfoClick) {
      PopupUtils.showUserDetailInfo(companyPick.userPromiseId)
    }
  }

  const PickCommentItem = (
    <Fragment>
      <div className="profile-wrap">
        <Button TP className="profile" style={{ paddingRight: '12px' }} onClick={onClickUserDetailInfoPopup}>
          <div className={!enabled ? 'profile-img-wrap blocked lhn29' : 'profile-img-wrap lhn29'}>
            <AdvImg src={companyPick.userProfileImg} srcMode={'small'} alt={companyPick.userNick} className="profile-img" loading="lazy"/>
          </div>
        </Button>
      </div>
      <div className={isMobile ? 'pick-contents' : 'pick-contents pc'}>
        <div className={!enabled ? 'nickname blocked' : 'nickname'}>
          <p className="company-pick-name">{companyPick.userNick}</p>
          <span className="date">{formatCreatedAtToElapsedTime({ createdAt: new Date(companyPick.createdAt) })}</span>
        </div>

        {companyPickContents}

        <div>
          <LikeButton
            id="companyPickLickBtn"
            className={`${companyPick.isLike ? 'active' : ''}`}
            count={companyPick.likeCount}
            onClick={onClickFavoriteCompanyPick}
          />
          <CommentButton
            id="companyPickCommentBtn"
            onClick={onClickCompanyPickCommentBtn}
            count={companyPick.commentCount}
          />
        </div>
      </div>

      <span className="line"/>
    </Fragment>
  )

  return (
    <UserPickWrap>
      <div className={matchInfoClass}>
        {SPORT_ID_GRAY_ICON_MAP[companyPick.sportId]}
        <span className="date">{dayjs(companyPick.dateOfMatch).format('MM/DD(ddd) HH:mm')}</span>
        <div className="team-info">
        <span className="team">
          {companyPick.homeTeamName}
        </span>
          <span className="vs">vs</span>
          <span className="team">
          {companyPick.awayTeamName}
        </span>
        </div>
      </div>
      <UserDetailItem className={className}>
        {PickCommentItem}
      </UserDetailItem>
    </UserPickWrap>
  )
})
